import React, { useState } from 'react';
import { FadeIn, CTAButton, Section, SectionTitle, Accent, pad2 } from './Shared';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { PRODUCTS } from './MetaPixel';
import { volumes, sessionFeatures, featureChips } from '../data/volumes';
import { bonuses } from '../data/bonuses';
import { Check, AlertTriangle, Plus, PrinterIcon, ArrowRightIcon, Lock, ZapIcon, ShieldCheck, BookOpenIcon, GiftIcon } from 'lucide-react';
export const ProductTomos = () => {
  const [openVolume, setOpenVolume] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-28" data-mp="2cpykdjm5u63c">
      <FadeIn data-id="element-29" data-mp="2cox0cpn6299h">
        <SectionTitle sub="Un volume ogni due mesi. Lo apri, scegli la sessione, insegni." data-id="element-30" data-mp="2cof43t0i7v1k">
          6 <Accent data-id="element-31" data-mp="30ercf4znvtso">volumi</Accent> completi
        </SectionTitle>
      </FadeIn>

      {/* Year timeline */}
      <FadeIn delay={0.05} className="mx-auto mb-8 max-w-4xl" data-id="element-32" data-mp="357u4t9spcx7a">
        <div className="grid grid-cols-6 gap-1.5 md:gap-2" aria-label="Il tuo anno, volume per volume" data-id="element-33" data-mp="2hktiqoasyglq">
          {volumes.map(v => <div key={v.number} data-id="element-34" data-mp="2cnt963equvpv">
              <div className={`h-1.5 rounded-full ${v.bar}`} data-id="element-35" data-mp="2cknl8rhupzvq" />
              <p className={`mt-2 font-display text-[11px] font-extrabold uppercase md:text-sm ${v.text}`} data-id="element-36" data-mp="2rc691bgycypn">
                Vol. {v.number}
              </p>
              <p className="hidden text-xs text-slate-500 md:block" data-id="element-37" data-mp="30bt299zj2y7m">{v.months}</p>
            </div>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.1} className="mb-12 md:mb-16" data-id="element-38" data-mp="2w9fwilks70au">
        <ul className="mx-auto flex max-w-3xl flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-39" data-mp="2wcu2e3snlrn1">
          {featureChips.map(chip => <li key={chip} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium text-slate-200 md:text-sm" data-id="element-40" data-mp="35bfg9ln6kihh">
              <Check size={12} className="text-sky-400" strokeWidth={3} aria-hidden="true" data-id="element-41" data-mp="1axeh0znpxtgv" />
              {chip}
            </li>)}
        </ul>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-xs text-slate-400 md:text-sm" data-id="element-42" data-mp="3b0pbtjhh1wuq">
          <PrinterIcon size={14} aria-hidden="true" data-id="element-43" data-mp="3fyqx8b2de89d" />
          <span data-id="element-44" data-mp="3jzsyexfukudz"><span className="font-semibold text-slate-200" data-id="element-45" data-mp="3jzsw2qsdkiat">Pronto da stampare</span> o da usare direttamente da smartphone o tablet.</span>
        </p>
      </FadeIn>

      <h3 className="mb-5 text-center text-xs font-semibold uppercase tracking-widest text-slate-400" data-id="element-46" data-mp="3ty9to6ltjv5u">
        Cosa ricevi
      </h3>
      <div className="mb-16 grid grid-cols-1 items-start gap-3 md:mb-24 md:grid-cols-2 md:gap-4 lg:grid-cols-3" data-id="element-47" data-mp="2r5nfdxi0cyh">
        {volumes.map((v, i) => {
        const isOpen = openVolume === i;
        return <FadeIn key={v.number} delay={Math.min(i * 0.04, 0.2)} data-id="element-48" data-mp="bkioj35pukvn" data-mpi={i}>
              <button type="button" onClick={() => setOpenVolume(isOpen ? null : i)} className={`group w-full rounded-2xl border border-t-[3px] bg-white/[0.03] p-5 text-left transition-[border-color,background-color] duration-200 ease-out hover:bg-white/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 md:p-6 ${v.border} ${isOpen ? 'border-x-white/20 border-b-white/20' : 'border-x-white/10 border-b-white/10'}`} aria-expanded={isOpen} data-id="element-49" data-mp="gjdj12d1pxvl">
                <div className="flex items-start justify-between gap-3" data-id="element-50" data-mp="hndw6ohlwbvl">
                  <div className="min-w-0" data-id="element-51" data-mp="1ecv7bizcyq4o">
                    <p className={`font-display text-xs font-extrabold uppercase tracking-wide ${v.text}`} data-id="element-52" data-mp="1ebjvcvvrec2h">
                      Volume {v.number} · {v.months}
                    </p>
                    <h4 className="mt-1.5 font-display text-lg font-extrabold leading-tight text-white md:text-xl" data-id="element-53" data-mp="14ma5lmoe15hj">
                      {v.title}
                    </h4>
                    <p className="mt-1 text-sm text-slate-400" data-id="element-54" data-mp="1x34b2xasmpyh">{v.tagline}</p>
                  </div>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/5 text-white transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-55" data-mp="1scw8kzjjbzko">
                    <Plus size={16} data-id="element-56" data-mp="1htzi2r5uhltw" />
                  </span>
                </div>
                <div className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-57" data-mp="13u7y89sndb7z">
                  <div className="overflow-hidden" data-id="element-58" data-mp="17xcys6t632tz">
                    <p className="pt-4 text-[15px] leading-relaxed text-slate-300" data-id="element-59" data-mp="1vm5npjbdgaf3">
                      {v.desc}
                    </p>
                    <p className="mt-3 inline-flex rounded-md bg-white/5 px-2 py-0.5 text-xs font-semibold text-slate-200" data-id="element-60" data-mp="20mbg51pgu0y8">
                      {v.sessions}
                    </p>
                  </div>
                </div>
              </button>
            </FadeIn>;
      })}
      </div>

      <FadeIn data-id="element-61" data-mp="2eip1ilx4emhz">
        <div className="grid gap-8 rounded-3xl border border-white/10 bg-slate-900/60 p-6 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:grid-rows-[auto_1fr] md:gap-x-12 md:gap-y-6 md:p-12" data-id="element-62" data-mp="2ei5i10rtnq1u">
          <div className="flex flex-col md:col-start-1 md:row-start-1" data-id="element-63" data-mp="7tn33ydtlx38">
            <h3 className="font-display text-3xl font-black uppercase italic leading-[0.95] tracking-tight text-white md:text-5xl md:leading-[1]" data-id="element-64" data-mp="2w3mwsxkba8n">
              Ogni sessione <Accent data-id="element-65" data-mp="clwod13zq8z6">include</Accent>
            </h3>
          </div>
          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 md:col-start-2 md:row-span-2 md:row-start-1" data-id="element-66" data-mp="cosmoqjr2zo5">
            {sessionFeatures.map(feature => <li key={feature.title} className="flex items-start gap-3" data-id="element-67" data-mp="3g48103z5vvf3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15" data-id="element-68" data-mp="3v0yujwm5mcws">
                  <Check size={12} className="text-emerald-400" strokeWidth={3} aria-hidden="true" data-id="element-69" data-mp="3q0jhz79p59fv" />
                </span>
                <div data-id="element-70" data-mp="1a8dixylca8h8">
                  <p className="font-semibold leading-snug text-white" data-id="element-71" data-mp="1ac19covhmerq">{feature.title}</p>
                  <p className="text-sm leading-normal text-slate-400" data-id="element-72" data-mp="15d80xav25dyf">{feature.desc}</p>
                </div>
              </li>)}
          </ul>
          <p className="text-lg font-semibold leading-snug text-sky-300 md:col-start-1 md:row-start-2 md:self-end md:text-xl" data-id="element-73" data-mp="1hclp9sem55oq">
            Apri il volume, scegli la sessione e insegna. Così semplice. 365
            lezioni ad alte prestazioni. Pronte oggi.
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  return <Section className="bg-slate-900/40" data-id="element-74" data-mp="tlpqgbbqpa7z">
      <FadeIn data-id="element-75" data-mp="ymdmjbetsh92">
        <SectionTitle sub="Materiali esclusivi, senza alcun costo aggiuntivo" data-id="element-76" data-mp="ynyk79wfsgb5">
          + 5 <Accent data-id="element-77" data-mp="13lqbc1p9byea">bonus</Accent> strategici
        </SectionTitle>
      </FadeIn>

      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 md:mb-10 md:gap-4 lg:grid-cols-5" data-id="element-78" data-mp="18cgh3g1li39i">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.04, 0.2)} className={i === bonuses.length - 1 ? 'sm:col-span-2 lg:col-span-1' : ''} data-id="element-79" data-mp="aw9mtf4rd1nc" data-mpi={i}>
            <article className="flex h-full gap-4 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-3 sm:flex-col sm:gap-0 sm:p-0" data-id="element-80" data-mp="fub5w026p0yt">
              <img src={bono.image} alt={`Copertina Bonus ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={1024} height={1024} className="aspect-square w-28 shrink-0 self-start rounded-xl object-cover sm:w-full sm:rounded-none" data-id="element-81" data-mp="2p4u33hyz5s16" />
              <div className="flex min-w-0 flex-1 flex-col sm:p-4" data-id="element-82" data-mp="2f90f92ft2c43">
                <p className="font-display text-xs font-extrabold uppercase tracking-wide text-sky-400" data-id="element-83" data-mp="2a8mhqebtykkb">
                  Bonus {bono.num}
                </p>
                <h3 className="mt-1 font-display text-base font-extrabold leading-tight text-white" data-id="element-84" data-mp="21f9fglrvm6lk">
                  {bono.title}
                </h3>
                <p className="mt-1.5 text-sm leading-snug text-slate-400" data-id="element-85" data-mp="1wi9bcgel7dx3">
                  {bono.desc}
                </p>
                <div className="mt-auto flex items-center justify-between gap-2 pt-3" data-id="element-86" data-mp="1rj63ypb4wvly">
                  <span className="text-sm text-slate-500 line-through" data-id="element-87" data-mp="1mkbn3swq9h4h">
                    {bono.value}
                  </span>
                  <span className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-400" data-id="element-88" data-mp="3fdrgzt0ljxzd">
                    Gratis
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn className="mx-auto flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-6 py-5 text-center md:flex-row md:text-left" data-id="element-89" data-mp="2rogo0xphn7vs">
        <div className="flex items-center gap-3" data-id="element-90" data-mp="2inqszw81xlj4">
          <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 md:flex" data-id="element-91" data-mp="2ipbr1vtkrmlq">
            <GiftIcon size={22} className="text-emerald-400" aria-hidden="true" data-id="element-92" data-mp="2nkr3cjbxpag1" />
          </span>
          <div data-id="element-93" data-mp="28rnvx4s24d31">
            <p className="text-sm text-slate-300" data-id="element-94" data-mp="28rxdc3z1dbx4">Valore totale dei 5 bonus: <span className="text-slate-500 line-through" data-id="element-95" data-mp="2do5ybxzwzzc7">50€</span></p>
            <p className="text-xs text-slate-500" data-id="element-96" data-mp="1u243so4n18s5">Inclusi in Megapack Funzionale 365, senza costi aggiuntivi.</p>
          </div>
        </div>
        <p className="font-display text-3xl font-black uppercase italic leading-none tracking-tight text-emerald-400 md:text-4xl" data-id="element-97" data-mp="1q33ohfldco9w">
          Gratis oggi
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  const units = [{
    value: hours,
    label: 'ore'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'sec'
  }];
  return <Section className="bg-slate-950 !py-14 md:!py-20" data-id="element-98" data-mp="2mrt5l3p69cut">
      <FadeIn className="mx-auto max-w-xl text-center" data-id="element-99" data-mp="2mtdzqs7kqts5">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-400" data-id="element-100" data-mp="2htrkyec3l5h8">
          Prezzo di lancio — scade tra
        </p>
        <div className="mt-4 flex items-start justify-center gap-2 md:gap-3" role="timer" aria-live="off" data-id="element-101" data-mp="2wnmm0y60hqhv">
          {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-102" data-mp="2qy29a18zlyw8" data-mpi={i}>
              {i > 0 && <span className="pt-3 font-display text-4xl font-black text-slate-600 md:pt-4 md:text-5xl" aria-hidden="true" data-id="element-103" data-mp="2qytxgr098uch">:</span>}
              <div className="flex w-20 flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] py-3 md:w-24 md:py-4" data-id="element-104" data-mp="2h7fzworv64lz">
                <span className="font-display text-4xl font-black tabular-nums leading-none tracking-tight text-white md:text-5xl" data-id="element-105" data-mp="2d7fa67zbe6zm">
                  {pad2(unit.value)}
                </span>
                <span className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-500" data-id="element-106" data-mp="34yd39d5lftlm">
                  {unit.label}
                </span>
              </div>
            </React.Fragment>)}
        </div>

        <p className="mt-6 flex items-center justify-center gap-2 text-base font-semibold text-amber-300 md:text-lg" data-id="element-107" data-mp="2vw3vgv1pmebx">
          <AlertTriangle size={18} aria-hidden="true" data-id="element-108" data-mp="1pegpmj2bn4jy" />
          Dopo, il prezzo sale. Per sempre.
        </p>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left" data-id="element-109" data-mp="3w4sdj7eerttq">
          <div className="mb-3 flex justify-between gap-3 text-sm text-slate-300" data-id="element-110" data-mp="4yijqqbn30oz">
            <span className="flex items-center gap-2" data-id="element-111" data-mp="4wejcicy8jtg">
              <span className="h-2 w-2 rounded-full bg-sky-400 animate-pulse" aria-hidden="true" data-id="element-112" data-mp="8z1dwr108iuc" />
              Posti a prezzo di lancio
            </span>
            <span className="font-semibold text-sky-400" data-id="element-113" data-mp="dsnevlxt2yab">Ne restano pochi</span>
          </div>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={71} aria-valuemin={0} aria-valuemax={100} aria-label="Posti già occupati" data-id="element-114" data-mp="is9svxlgkik6">
            <div className="h-full rounded-full bg-blue-500" style={{
            width: '71%'
          }} data-id="element-115" data-mp="sm8tut9spwaj" />
          </div>
          <p className="text-right text-xs text-slate-500" data-id="element-116" data-mp="2ufpzjred8n2n">
            71% dei posti già occupati
          </p>
        </div>
      </FadeIn>
    </Section>;
};
export const OfferCard = ({
  id,
  footnote
}: {
  id?: string;
  footnote: string;
}) => {
  const includes = [{
    icon: BookOpenIcon,
    label: '6 Volumi Completi',
    detail: '365 sessioni pronte da insegnare',
    price: '69€'
  }, {
    icon: GiftIcon,
    label: '5 Bonus Esclusivi',
    detail: 'Adattamenti, riscaldamenti, chiusure e frasi',
    price: '50€'
  }];
  return <div id={id} className="relative rounded-3xl border border-sky-400/40 bg-slate-900 p-5 shadow-2xl shadow-blue-950/50 md:p-8" data-id="element-117" data-mp="akv17nd5gs8q">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-blue-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-white" data-id="element-118" data-mp="ojclfb3ujmlw">
        Il più scelto
      </div>

      <p className="mt-2 text-center font-display text-xl font-black uppercase italic tracking-tight text-white md:text-2xl" data-id="element-119" data-mp="tc5m9nw72asp">
        Megapack Funzionale <Accent data-id="element-120" data-mp="17cosqql0h8sr">365™</Accent>
      </p>

      <ul className="mt-5 divide-y divide-white/10 border-y border-white/10" data-id="element-121" data-mp="179kgr71b18rj">
        {includes.map(({
        icon: Icon,
        label,
        detail,
        price
      }) => <li key={label} className="flex items-center justify-between gap-3 py-3" data-id="element-122" data-mp="1lz5cmsrra2k9">
            <div className="flex min-w-0 items-start gap-3" data-id="element-123" data-mp="1qwdyb3c44oq9">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5" data-id="element-124" data-mp="1cy7gsxrh11cr">
                <Icon size={16} className="text-sky-400" aria-hidden="true" data-id="element-125" data-mp="1gyzrzwjwlgbo" />
              </span>
              <div className="min-w-0" data-id="element-126" data-mp="137mbysl7f0le">
                <p className="text-sm font-semibold leading-snug text-white md:text-base" data-id="element-127" data-mp="136jn0q7fz884">{label}</p>
                <p className="text-xs leading-snug text-slate-400 md:text-sm" data-id="element-128" data-mp="180ff5c10y0vd">{detail}</p>
              </div>
            </div>
            <span className="shrink-0 text-sm text-slate-500 line-through" data-id="element-129" data-mp="t83s0c5anetc">{price}</span>
          </li>)}
      </ul>

      <div className="mt-6 text-center" data-id="element-130" data-mp="k9a3za613wre">
        <p className="text-sm text-slate-400" data-id="element-131" data-mp="kal3uywcskd8">
          Valore originale <span className="text-slate-500 line-through" data-id="element-132" data-mp="p83fix1r9dox">119€</span> · Accesso completo oggi a
        </p>
        <p className="mt-2 font-display font-black italic leading-none tracking-tight text-white" data-id="element-133" data-mp="h86w97tpsh39">
          <span className="text-7xl md:text-8xl" data-id="element-134" data-mp="c82e7dd5reax">19</span>
          <span className="align-top text-3xl md:text-4xl" data-id="element-135" data-mp="canhm6t3u2as">€</span>
          <span className="ml-1 text-base font-bold not-italic text-slate-400" data-id="element-136" data-mp="7gja9mfxfs96">EUR</span>
        </p>
        <p className="mt-2 text-xs text-slate-400 md:text-sm" data-id="element-137" data-mp="3fh8axuml23i">{footnote}</p>
      </div>

      <div className="mt-6" data-id="element-138" data-mp="3unlbpx37u0x1">
        <CTAButton href={PRODUCTS.mf365.checkoutUrl} fullWidth trackingProduct="mf365" onClick={markCheckoutStarted} data-id="element-139" data-mp="3unv3nq7tmi5d">
          Lo voglio adesso
          <ArrowRightIcon size={18} className="transition-transform duration-200 group-hover/cta:translate-x-0.5" aria-hidden="true" data-id="element-140" data-mp="3ko4apsywsb7e" />
        </CTAButton>
        <LocalCurrencyNote className="mt-4" data-id="element-141" data-mp="3golngqjm7o1e" />
        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-slate-500" data-id="element-142" data-mp="1ixj10dmmlz13">
          <span className="flex items-center gap-1" data-id="element-143" data-mp="1ev5x7urfwb6y"><Lock size={12} aria-hidden="true" data-id="element-144" data-mp="1ew755qd5shkg" /> Pagamento sicuro</span>
          <span className="flex items-center gap-1" data-id="element-145" data-mp="2wrzf8mbz1nhv"><ZapIcon size={12} aria-hidden="true" data-id="element-146" data-mp="2wr7xaebaroa2" /> Accesso in pochi minuti</span>
          <span className="flex items-center gap-1" data-id="element-147" data-mp="31qasz85e0i5d"><ShieldCheck size={12} aria-hidden="true" data-id="element-148" data-mp="36j3xpw0uvqhg" /> Garanzia 7 giorni</span>
        </div>
      </div>
    </div>;
};
export const Pricing = () => {
  return <Section className="bg-slate-900/40" id="pricing" data-id="element-149" data-mp="3ajxhcd79mj4j">
      <div className="mx-auto max-w-lg" data-id="element-150" data-mp="2dupw76ak6c15">
        <FadeIn className="mb-10 text-center" data-id="element-151" data-mp="2dvrcbp6flopq">
          <p className="text-sm text-slate-400 md:text-base" data-id="element-152" data-mp="2iszrdgwa9cmr">
            Sei a un passo dall’avere ogni lezione già risolta…
          </p>
          <h2 className="mx-auto mt-3 font-display text-4xl font-black uppercase italic leading-[0.92] tracking-tight text-white md:text-[56px] md:leading-[1]" data-id="element-153" data-mp="2mu35nyyon0lv">
            Offerta speciale di <Accent data-id="element-154" data-mp="23lyql3lbz7n">lancio</Accent>
          </h2>
          <p className="mt-3 text-sm text-slate-400" data-id="element-155" data-mp="2701hug62afm">
            Volumi, bonus e 365 sessioni pronte in un unico accesso.
          </p>
        </FadeIn>

        <FadeIn delay={0.05} data-id="element-156" data-mp="235txjru5kxst">
          <OfferCard footnote="Pagamento unico · Nessun abbonamento · Accesso immediato" data-id="element-157" data-mp="23521kfwn8y20" />
        </FadeIn>
      </div>
    </Section>;
};